import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { KpiFieldMeta } from "../Types/MLAnalyticsTypes";

interface KpiRadarChartProps {
  kpiFields: KpiFieldMeta[];
  employeeValues: Record<string, number | null | undefined>;
  teamAverages: Record<string, number | null | undefined>;
}

/**
 * Normalizes each raw KPI field onto a common 0-100 "higher is better" scale
 * so ratings (0-5), a percentage (utilization), and raw counts (late marks,
 * noncompliance issues, unpaid leave — where lower is better) can share one
 * radar chart axis.
 */
const normalize = (field: string, value: number | null | undefined): number => {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;

  switch (field) {
    case "avg_manager_kpi_rating_filled":
    case "avg_self_kpi_rating_filled":
    case "avg_manager_360_rating_filled":
      // 0-5 rating scale
      return Math.max(0, Math.min(100, value * 20));
    case "avg_utilization_filled":
      // already a 0-100 percentage
      return Math.max(0, Math.min(100, value));
    case "avg_late_marks_filled":
      return Math.max(0, 100 - value * 10);
    case "noncompliance_issue_count_filled":
      return Math.max(0, 100 - value * 15);
    case "total_leave_without_pay_filled":
      return Math.max(0, 100 - value * 10);
    default:
      return Math.max(0, Math.min(100, value));
  }
};

/** Radar chart plotting an employee's normalized KPI features against the team average. */
const KpiRadarChart: React.FC<KpiRadarChartProps> = ({ kpiFields, employeeValues, teamAverages }) => {
  const data = kpiFields.map(({ field, label }) => ({
    label,
    Employee: normalize(field, employeeValues[field]),
    "Team Average": normalize(field, teamAverages[field]),
  }));

  return (
    <Box sx={{ width: "100%", height: 320 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Normalized 0-100 scale (higher is better).
      </Typography>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="70%">
          <PolarGrid />
          <PolarAngleAxis dataKey="label" tick={{ fontSize: 11 }} />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
          <Radar name="Employee" dataKey="Employee" stroke="#1976d2" fill="#1976d2" fillOpacity={0.4} />
          <Radar name="Team Average" dataKey="Team Average" stroke="#ed6c02" fill="#ed6c02" fillOpacity={0.2} />
          <Legend />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default KpiRadarChart;
